import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import * as yup from 'yup';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import RingilCalendar from '@components/framework/input/RingilCalendar.tsx';
import {
  dayjsNowInTz,
  dayjsFromLocalDateString,
  dayjsFromApiUtcInTz,
  stringYMDFromDayjs,
} from '@utils/dateWithTz.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { AllowedCarPlateMatchTypeEnum, SaveUpdateAllowedCarPlateRequest } from '@api/ringil-api.v2.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';

interface ComponentProps {
  onSaved: () => void;
  carPlateId?: string;
}

interface Form {
  id?: string;
  carPlate: string;
  matchType: AllowedCarPlateMatchTypeEnum;
  firstName?: string;
  secondName?: string;
  cardId?: string;
  sinceLocalDate: string;
  tillLocalDate: string;
  utcStart?: string;
  utcEnd?: string;
  timezoneId?: string;
  validSince?: string;
  validTill?: string;
}

export const useCreateAddCarPlateSidebar = () => {
  const [visible, setVisible] = useState(false);
  const [carPlateId, setCarPlateId] = useState<string | undefined>(undefined);
  const [initialCarPlate, setInitialCarPlate] = useState<string | undefined>(undefined);

  const openSidebar = (carPlate?: string, carPlateIdToEdit?: string) => {
    setInitialCarPlate(carPlate);
    setCarPlateId(carPlateIdToEdit);
    setVisible(true);
  };

  const AddCarPlateSidebar: React.FC<ComponentProps> = ({ onSaved, carPlateId }) => {
    const { saveUpdateAllowedCarPlate, getAllowedCarPlate } = useContext(ApiContext);
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { tr } = useTranslationLgs();
    const { setBeValidationErrors, BackendValidationComponent } = useBEValidation();
    const [loading, setLoading] = useState<boolean>(false);

    const timezoneToUse = loggedUser?.defaultTimezoneId ?? 'Europe/Prague';

    const form = useForm<Form>(
      yup.object().shape({
        carPlate: yup.string().required(tr('validation.required', 'Value is required')).min(3, tr('AddCarPlateSidebar.spzMinLength', 'SPZ musí mít alespoň 3 znaky')),
        sinceLocalDate: yup.string().test('sinceLocalDate', 'validation.required', value => dayjsFromLocalDateString(value)?.isValid() ?? false),
      }),
      {
        id: undefined,
        carPlate: '',
        matchType: AllowedCarPlateMatchTypeEnum.EXACT,
        firstName: null,
        secondName: null,
        cardId: null,
        sinceLocalDate: dayjsNowInTz(timezoneToUse).format('YYYY-MM-DD'),
        tillLocalDate: null,
        utcStart: undefined,
        utcEnd: undefined,
        timezoneId: timezoneToUse,
        validSince: undefined,
        validTill: undefined,
      },
      d => save(d),
      false,
      false,
    );

    useEffect(() => {
      if (carPlateId && visible) {
        setLoading(true);
        getAllowedCarPlate(carPlateId, (data) => {
          const hasNewFields = isNotNullOrUndefined(data.utcStart) && isNotNullOrUndefined(data.timezoneId);
          const validFromUtc = hasNewFields ? data.utcStart : data.validSince;
          const validToUtc = hasNewFields ? data.utcEnd : data.validTill;
          const tz = hasNewFields ? data.timezoneId : 'Europe/Prague';

          const dayjsFrom = dayjsFromApiUtcInTz(validFromUtc, tz);
          const dayjsTo = isNotNullOrUndefined(validToUtc) ? dayjsFromApiUtcInTz(validToUtc, tz) : null;

          form.setFieldValue(form.names.id, data.id);
          form.setFieldValue(form.names.carPlate, data.carPlate);
          form.setFieldValue(form.names.matchType, data.allowedCarPlateMatchType ?? AllowedCarPlateMatchTypeEnum.EXACT);
          form.setFieldValue(form.names.firstName, data.firstName);
          form.setFieldValue(form.names.secondName, data.secondName);
          form.setFieldValue(form.names.cardId, data.cardId);
          form.setFieldValue(form.names.sinceLocalDate, stringYMDFromDayjs(dayjsFrom));
          form.setFieldValue(form.names.tillLocalDate, dayjsTo ? stringYMDFromDayjs(dayjsTo) : null);
          form.setFieldValue(form.names.utcStart, data.utcStart);
          form.setFieldValue(form.names.utcEnd, data.utcEnd);
          form.setFieldValue(form.names.timezoneId, data.timezoneId);
          form.setFieldValue(form.names.validSince, data.validSince);
          form.setFieldValue(form.names.validTill, data.validTill);
          setLoading(false);
        });
      } else if (!carPlateId && visible) {
        form.setFieldValue(form.names.id, undefined);
        form.setFieldValue(form.names.carPlate, initialCarPlate || '');
        form.setFieldValue(form.names.matchType, AllowedCarPlateMatchTypeEnum.EXACT);
        form.setFieldValue(form.names.cardId, null);
        form.setFieldValue(form.names.firstName, null);
        form.setFieldValue(form.names.secondName, null);
        form.setFieldValue(form.names.sinceLocalDate, dayjsNowInTz(timezoneToUse).format('YYYY-MM-DD'));
        form.setFieldValue(form.names.tillLocalDate, null);
        form.setFieldValue(form.names.utcStart, undefined);
        form.setFieldValue(form.names.utcEnd, undefined);
        form.setFieldValue(form.names.timezoneId, timezoneToUse);
        form.setFieldValue(form.names.validSince, undefined);
        form.setFieldValue(form.names.validTill, undefined);
      }
    }, [carPlateId, visible, initialCarPlate]);

    const save = (data: Form) => {
      setLoading(true);

      const payload: SaveUpdateAllowedCarPlateRequest = {
        id: data.id,
        carPlate: data.carPlate,
        allowedCarPlateMatchType: data.matchType,
        firstName: data.firstName,
        secondName: data.secondName,
        cardId: data.cardId,
        sinceLocalDate: data.sinceLocalDate,
        tillLocalDate: data.tillLocalDate,
        utcStart: data.sinceLocalDate,
        utcEnd: data.tillLocalDate,
        timezoneId: timezoneToUse,
      };

      saveUpdateAllowedCarPlate(
        payload,
        () => {
          setLoading(false);
          setVisible(false);
          onSaved();
        },
        null,
        {
          onValidationFailed: (errors) => {
            setLoading(false);
            setBeValidationErrors(errors);
          },
        },
      );
    };

    return (
      <>
        <RingilSidebar
          visible={visible}
          onContinue={() => {
            form.validateAndSend();
          }}
          onBack={() => {
            setVisible(false);
          }}
          heading={carPlateId ? tr('AddCarPlateSidebar.upravitSpz', 'Upravit SPZ') : tr('AddCarPlateSidebar.pridatSpz', 'Přidat SPZ')}
          loading={loading}
        >
          <div className={'flex flex-col gap-4'}>
            <BackendValidationComponent />
            <div className={'flex items-center gap-2'}>
              <span>{tr('AddCarPlateSidebar.timezone', 'Timezone:')}</span>
              <COTimezone timezoneId={timezoneToUse} />
            </div>
            <Field label={tr('AddCarPlateSidebar.spz', 'SPZ')} required={true} errorMessage={findErrorMessage(form.validationErrors, 'carPlate', tr)}>
              <RingilInputText value={form.values.carPlate} onChange={v => form.setFieldValue(form.names.carPlate, v)} />
            </Field>
            <Field label={tr('AddCarPlateSidebar.matchType', 'Typ shody')}>
              <Dropdown
                value={form.values.matchType}
                onChange={e => form.setFieldValue(form.names.matchType, e.value)}
                options={Object.values(AllowedCarPlateMatchTypeEnum).map(value => ({ value, label: tr(`CarPlateMatchType.${value}`, value) }))}
              />
            </Field>
            <Field label={tr('AddCarPlateSidebar.firstName', 'First name')}>
              <RingilInputText value={form.values.firstName} onChange={v => form.setFieldValue(form.names.firstName, v)} />
            </Field>
            <Field label={tr('AddCarPlateSidebar.secondName', 'Second name')}>
              <RingilInputText value={form.values.secondName} onChange={v => form.setFieldValue(form.names.secondName, v)} />
            </Field>
            <Field label={tr('AddCarPlateSidebar.cardId', 'Card ID')}>
              <RingilInputText value={form.values.cardId} onChange={v => form.setFieldValue(form.names.cardId, v)} />
            </Field>

            <Field label={tr('AddCarPlateSidebar.platnostOd', 'Platnost od')} required errorMessage={findValidationMessage(form.validationErrors, 'sinceLocalDate', tr)}>
              <RingilCalendar value={form.values.sinceLocalDate} onChange={v => form.setFieldValue(form.names.sinceLocalDate, v)} fullWidth />
            </Field>
            <Field label={tr('AddCarPlateSidebar.platnostDo', 'Platnost do')}>
              <RingilCalendar value={form.values.tillLocalDate} onChange={v => form.setFieldValue(form.names.tillLocalDate, v)} fullWidth />
            </Field>
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { AddCarPlateSidebar, visible, setVisible, setCarPlateId, carPlateId, openSidebar };
};
